import React, { useCallback, useEffect, useState } from 'react';
import {
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ListFilter,
  Loader2,
  Mail,
  RefreshCw,
  Users,
  Target,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  CAMPAIGN_HISTORY_DEFAULT_PAGE_SIZE,
  formatCampaignHistoryStatus,
  type CampaignHistoryTypeFilter,
} from '../lib/campaignHistory';
import { dbService } from '../lib/supabase';

type HistoryRow = Awaited<ReturnType<typeof dbService.getCampaignHistoryRows>>['rows'][number];
type HistoryTypeFilter = CampaignHistoryTypeFilter;

export interface CampaignHistoryPanelProps {
  embedded?: boolean;
}

const TYPE_FILTERS: { id: HistoryTypeFilter; label: string }[] = [
  { id: 'all', label: 'All' },
  { id: 'sponsor_invite', label: 'Sponsor invites' },
  { id: 'cdmo_claim', label: 'CDMO claim' },
];

const CampaignHistoryPanel: React.FC<CampaignHistoryPanelProps> = ({ embedded = false }) => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<HistoryRow[]>([]);
  const [totalCdmoClaimed, setTotalCdmoClaimed] = useState(0);
  const [totalSponsorSignUps, setTotalSponsorSignUps] = useState(0);
  const [sponsorCampaignCount, setSponsorCampaignCount] = useState(0);
  const [cdmoCampaignCount, setCdmoCampaignCount] = useState(0);
  const [typeFilter, setTypeFilter] = useState<HistoryTypeFilter>('all');
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});

  const pageSize = CAMPAIGN_HISTORY_DEFAULT_PAGE_SIZE;

  const fetchHistory = useCallback(
    async (nextPage: number, nextFilter: HistoryTypeFilter) => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await dbService.getCampaignHistoryRows({
          page: nextPage,
          pageSize,
          typeFilter: nextFilter,
        });
        setRows(data.rows);
        setPage(data.page);
        setTotalCount(data.totalCount);
        setTotalPages(data.totalPages);
        setTotalCdmoClaimed(data.totalCdmoClaimedFromCampaign);
        setTotalSponsorSignUps(data.totalSponsorSignUps);
        setSponsorCampaignCount(data.sponsorCampaignCount);
        setCdmoCampaignCount(data.cdmoCampaignCount);
      } catch (e) {
        console.error(e);
        setLoadError('Could not load campaign history. Check console or database setup.');
      } finally {
        setLoading(false);
      }
    },
    [pageSize],
  );

  useEffect(() => {
    void fetchHistory(page, typeFilter);
  }, [page, typeFilter, fetchHistory]);

  const handleTypeFilterChange = (next: HistoryTypeFilter) => {
    setExpandedRows({});
    setTypeFilter(next);
    setPage(1);
  };

  const handlePageChange = (nextPage: number) => {
    setExpandedRows({});
    setPage(nextPage);
  };

  const handleRefresh = () => {
    setExpandedRows({});
    void fetchHistory(page, typeFilter);
  };

  const toggleRow = (id: string) => {
    setExpandedRows(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const formatTypeLabel = (row: HistoryRow) =>
    row.campaignType === 'sponsor_invite' ? 'Sponsor invite' : 'CDMO claim';

  const formatStatus = (status: string) => formatCampaignHistoryStatus(status);

  const metricCell = (value: number) => (
    <span className="tabular-nums font-medium text-charcoal">{value}</span>
  );

  const statusBadgeClass = (status: string) => {
    const s = status.toLowerCase();
    if (s.includes('account') || s.includes('convert') || s.includes('claimed')) {
      return 'bg-green-100 text-green-800';
    }
    if (s.includes('unsubscrib') || s === 'failed') {
      return 'bg-red-50 text-red-700';
    }
    if (s === 'active') {
      return 'bg-blue-100 text-blue-800';
    }
    return 'bg-slate-100 text-slate-600';
  };

  const rowDetail = (row: HistoryRow) => {
    if ('detailLabel' in row && row.detailLabel) return row.detailLabel;
    if ('companyName' in row && row.companyName) return row.companyName;
    return null;
  };

  const emptyMessage = () => {
    if (totalCount === 0 && typeFilter === 'sponsor_invite') {
      return 'No sponsor invite batches yet. Start one on the Sponsor invites tab.';
    }
    if (totalCount === 0 && typeFilter === 'cdmo_claim') {
      return 'No CDMO claim campaigns yet. Start one on the CDMO claim tab.';
    }
    if (totalCount === 0 && sponsorCampaignCount === 0 && cdmoCampaignCount === 0) {
      return 'No campaign history yet. Start a campaign on the CDMO claim or Sponsor invites tab.';
    }
    return 'No campaigns match this filter.';
  };

  const rangeStart = totalCount > 0 ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = totalCount > 0 ? Math.min(page * pageSize, totalCount) : 0;

  const typeBadgeClass = (row: HistoryRow) =>
    row.campaignType === 'sponsor_invite'
      ? 'bg-sky-blue/15 text-electric-blue border-sky-blue/30'
      : 'bg-lime-green/10 text-green-900 border-lime-green/25';

  /**
   * Renders Sign-ups / claimed column with identities of those who converted.
   */
  const renderSignUpsCell = (row: HistoryRow) => {
    const { signUps, conversions } = row;
    
    if (signUps <= 0) {
      return <span className="text-slate-gray tabular-nums opacity-50">0</span>;
    }

    // If it's a CDMO claim, we know exactly which CDMO it is from the row itself
    const label = signUps === 1 ? '1 conversion' : `${signUps} conversions`;
    
    return (
      <div className="flex flex-col gap-1">
        <span className="inline-flex items-center gap-1.5 font-medium text-green-700" title={label}>
          <CheckCircle2 className="w-4 h-4 text-lime-green shrink-0" strokeWidth={2.5} aria-hidden />
          <span className="tabular-nums">{signUps}</span>
        </span>
        {conversions && conversions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-0.5">
            {conversions.slice(0, 2).map((c, i) => (
              <span key={i} className="text-[10px] bg-lime-green/10 text-green-800 px-1.5 py-0.5 rounded-full border border-lime-green/20 truncate max-w-[100px]">
                {c.company || c.name || c.email}
              </span>
            ))}
            {conversions.length > 2 && (
              <span className="text-[10px] text-slate-gray">+{conversions.length - 2} more</span>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderExpandedContent = (row: HistoryRow) => {
    const { conversions, recipients } = row;
    
    return (
      <motion.div 
        initial={{ opacity: 0, height: 0 }}
        animate={{ opacity: 1, height: 'auto' }}
        exit={{ opacity: 0, height: 0 }}
        className="bg-charcoal/[0.03] border-b border-charcoal/10"
      >
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
          {/* Left side: Converted Identities */}
          <div>
            <h4 className="flex items-center gap-2 text-sm font-bold text-charcoal mb-4">
              <CheckCircle2 className="w-4 h-4 text-lime-green" />
              Conversions ({row.signUps})
            </h4>
            {conversions && conversions.length > 0 ? (
              <div className="space-y-3">
                {conversions.map((c, i) => (
                  <div key={i} className="bg-white p-3 rounded-lg border border-charcoal/10 shadow-sm flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-lime-green/10 flex items-center justify-center shrink-0">
                      <Target className="w-4 h-4 text-lime-green" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-bold text-charcoal text-sm truncate">{c.company || c.name}</p>
                      {c.name && c.company && <p className="text-xs text-slate-gray truncate">{c.name}</p>}
                      {'invitedEmail' in c && c.invitedEmail && (
                        <p className="text-[10px] text-slate-gray truncate" title={c.invitedEmail}>
                          Invited: {c.invitedEmail}
                        </p>
                      )}
                      {c.email && (
                        <p className="text-xs text-electric-blue truncate" title={c.email}>
                          Account: {c.email}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-gray italic py-2">No conversions yet for this campaign.</p>
            )}
          </div>

          {/* Right side: Recipient Status List */}
          <div>
            <h4 className="flex items-center gap-2 text-sm font-bold text-charcoal mb-4">
              <Users className="w-4 h-4 text-electric-blue" />
              Recipients Status
            </h4>
            <div className="max-h-[300px] overflow-y-auto space-y-2 pr-2 scrollbar-thin">
              {recipients && recipients.length > 0 ? (
                recipients.map((r, i) => (
                  <div key={i} className="flex items-center justify-between p-2.5 rounded-md bg-white/50 border border-charcoal/5 text-xs">
                    <div className="min-w-0 max-w-[180px]">
                      {'companyName' in r && r.companyName && (
                        <span className="truncate block font-semibold text-charcoal" title={r.companyName}>
                          {r.companyName}
                        </span>
                      )}
                      <span className="truncate block font-medium text-charcoal" title={r.email}>
                        {r.email}
                      </span>
                      {'signupEmail' in r && r.signupEmail && r.signupEmail !== r.email && (
                        <span className="truncate block text-[10px] text-slate-gray" title={String(r.signupEmail)}>
                          Signed up: {r.signupEmail}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-slate-gray text-[10px]">Stage {r.stage || 1}</span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                        r.status === 'converted' ? 'bg-lime-green/10 text-green-700 border-lime-green/20' :
                        r.status === 'clicked' ? 'bg-amber-100 text-amber-700 border-amber-200' :
                        r.status === 'unsubscribed' ? 'bg-red-50 text-red-600 border-red-100' :
                        'bg-slate-100 text-slate-600 border-slate-200'
                      }`}>
                        {r.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-sm text-slate-gray italic">No recipient data available.</p>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  const filterBar = (
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
      <div className="flex items-center gap-2 text-sm text-slate-gray">
        <ListFilter className="w-4 h-4 shrink-0" aria-hidden />
        <span>
          {totalCount > 0
            ? `Showing ${rangeStart}–${rangeEnd} of ${totalCount} record${totalCount !== 1 ? 's' : ''}`
            : 'No records'}
        </span>
      </div>
      <div
        className="flex flex-wrap gap-1 rounded-xl border border-charcoal/15 p-1 bg-charcoal/[0.02]"
        role="group"
        aria-label="Filter by campaign type"
      >
        {TYPE_FILTERS.map((f) => {
          const isActive = typeFilter === f.id;
          const count =
            f.id === 'all'
              ? sponsorCampaignCount + cdmoCampaignCount
              : f.id === 'sponsor_invite'
                ? sponsorCampaignCount
                : cdmoCampaignCount;
          return (
            <button
              key={f.id}
              type="button"
              onClick={() => handleTypeFilterChange(f.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive
                  ? 'bg-electric-blue text-white shadow-md'
                  : 'text-slate-gray hover:text-charcoal hover:bg-white'
              }`}
            >
              {f.label}
              <span className={`ml-1.5 text-xs ${isActive ? 'text-white/85' : 'text-slate-gray'}`}>
                ({count})
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );

  const header = embedded ? (
    <header className="mb-5 pb-5 border-b border-charcoal/10 shrink-0">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-lg font-bold text-charcoal flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-electric-blue shrink-0" aria-hidden />
            Campaign history
          </h2>
          <p className="mt-2 text-sm text-slate-gray leading-relaxed max-w-3xl">
            In-app metrics only: emails sent, link clicks, unsubscribes, and sign-ups or profile claims.
          </p>
        </div>
        <button
          type="button"
          onClick={handleRefresh}
          disabled={loading}
          aria-label="Refresh data"
          title="Refresh data"
          className="p-2 rounded-lg border border-slate-gray/20 text-electric-blue hover:bg-electric-blue/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} aria-hidden />
        </button>
      </div>
      
      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-gradient-to-br from-electric-blue/5 to-sky-blue/5 p-4 rounded-xl border border-electric-blue/10">
          <p className="text-xs text-slate-gray font-bold uppercase tracking-wider mb-1">Sponsor Conversions</p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-charcoal">{totalSponsorSignUps}</span>
            <span className="text-xs text-slate-gray">from {sponsorCampaignCount} batches</span>
          </div>
        </div>
        <div className="bg-gradient-to-br from-lime-green/5 to-green-100/10 p-4 rounded-xl border border-lime-green/10">
          <p className="text-xs text-slate-gray font-bold uppercase tracking-wider mb-1">Profiles Claimed</p>
          <div className="flex items-baseline gap-2">
            <span className="text-2xl font-black text-green-900">{totalCdmoClaimed}</span>
            <span className="text-xs text-slate-gray">from {cdmoCampaignCount} campaigns</span>
          </div>
        </div>
      </div>
      {loadError ? <p className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded border border-red-100">{loadError}</p> : null}
    </header>
  ) : null;

  const table = loading ? (
    <div className="flex justify-center py-24">
      <div className="flex flex-col items-center gap-3">
        <Loader2 className="w-10 h-10 animate-spin text-electric-blue" aria-hidden />
        <p className="text-sm text-slate-gray animate-pulse font-medium">Fetching campaign data...</p>
      </div>
    </div>
  ) : (
    <>
      {filterBar}
      <div className="overflow-hidden rounded-xl border border-charcoal/15 bg-white shadow-sm">
        <div className="overflow-x-auto overflow-y-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-charcoal/10 bg-charcoal/[0.03] text-left text-slate-gray uppercase text-[11px] tracking-widest font-black">
                <th className="p-3 font-black min-w-[140px]">Campaign</th>
                <th className="p-3 font-black whitespace-nowrap">Started</th>
                <th className="p-3 font-black text-center">Recipients</th>
                <th className="p-3 font-black text-center">Stage</th>
                <th className="p-3 font-black text-center">Sent</th>
                <th className="p-3 font-black text-center">Unsub</th>
                <th className="p-3 font-black text-center whitespace-nowrap">Link clicks</th>
                <th className="p-3 font-black">Sign-ups</th>
                <th className="p-3 font-black min-w-[120px]">Status</th>
                <th className="p-3 font-black w-10" aria-label="Expand row" />
              </tr>
            </thead>
            <tbody className="divide-y divide-charcoal/5">
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={10} className="p-12 text-center text-slate-gray">
                    <div className="flex flex-col items-center gap-2">
                      <Mail className="w-8 h-8 opacity-20" />
                      <p>{emptyMessage()}</p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => {
                  const rowId = `${row.campaignType}-${row.id}`;
                  const isExpanded = !!expandedRows[rowId];
                  const detail = rowDetail(row);
                  
                  return (
                    <React.Fragment key={rowId}>
                      <tr
                        onClick={() => toggleRow(rowId)}
                        className={`group cursor-pointer transition-colors duration-150 ${isExpanded ? 'bg-charcoal/[0.03]' : 'hover:bg-charcoal/[0.01]'}`}
                      >
                        <td className="p-4">
                          <div className="flex flex-col">
                            <span
                              className={`inline-flex items-center w-fit px-2 py-0.5 rounded-md text-[10px] font-bold border mb-1.5 tracking-tight ${typeBadgeClass(row)}`}
                            >
                              {formatTypeLabel(row).toUpperCase()}
                            </span>
                            {detail ? (
                              <span className="font-bold text-charcoal truncate max-w-[180px]" title={detail}>
                                {detail}
                              </span>
                            ) : (
                              <span className="text-slate-gray italic text-xs">No entity name</span>
                            )}
                          </div>
                        </td>
                        <td className="p-4 text-slate-gray whitespace-nowrap">
                          {row.dateStarted ? new Date(row.dateStarted).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                        </td>
                        <td className="p-4 text-center font-medium">
                          <span className="bg-charcoal/[0.05] px-2 py-1 rounded text-xs tabular-nums">
                            {row.recipientCount}
                          </span>
                        </td>
                        <td className="p-3 text-center font-medium">
                          <span className="text-xs">Stage {row.currentStage}</span>
                        </td>
                        <td className="p-3 text-center">{metricCell(row.sent ?? 0)}</td>
                        <td className="p-3 text-center">{metricCell(row.unsubscribed ?? 0)}</td>
                        <td className="p-3 text-center">{metricCell(row.linkClicks ?? 0)}</td>
                        <td className="p-3">{renderSignUpsCell(row)}</td>
                        <td className="p-3">
                          <span
                            className={`inline-block px-2 py-1 rounded-full text-[10px] font-bold leading-tight ${statusBadgeClass(row.status)}`}
                          >
                            {formatStatus(row.status)}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <button className="p-1.5 hover:bg-white rounded-full border border-transparent hover:border-charcoal/10 transition-all">
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-gray" /> : <ChevronDown className="w-5 h-5 text-slate-gray" />}
                          </button>
                        </td>
                      </tr>
                      <tr>
                        <td colSpan={10} className="p-0 border-none">
                          <AnimatePresence>
                            {isExpanded && renderExpandedContent(row)}
                          </AnimatePresence>
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
      {totalPages > 1 ? (
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4 pt-4 border-t border-charcoal/10 px-1">
          {!loading && rangeStart > 0 ? (
            <p className="text-sm text-slate-gray">
              Page {page} of {totalPages}
            </p>
          ) : (
            <span />
          )}
          <motion.div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => handlePageChange(Math.max(1, page - 1))}
              disabled={page === 1 || loading}
              className="p-2 rounded-lg border border-slate-gray/20 text-slate-gray hover:bg-slate-gray/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Previous page"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let p: number;
                if (totalPages <= 5) {
                  p = i + 1;
                } else if (page <= 3) {
                  p = i + 1;
                } else if (page >= totalPages - 2) {
                  p = totalPages - 4 + i;
                } else {
                  p = page - 2 + i;
                }
                return (
                  <button
                    key={p}
                    type="button"
                    onClick={() => handlePageChange(p)}
                    disabled={loading}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 ${
                      page === p
                        ? 'bg-electric-blue text-white'
                        : 'text-slate-gray hover:bg-slate-gray/10'
                    }`}
                  >
                    {p}
                  </button>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
              disabled={page === totalPages || loading}
              className="p-2 rounded-lg border border-slate-gray/20 text-slate-gray hover:bg-slate-gray/10 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              aria-label="Next page"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </motion.div>
        </div>
      ) : null}
    </>
  );

  if (embedded) {
    return (
      <div className="flex flex-col flex-1 min-h-0">
        {header}
        {table}
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-electric-blue/20 bg-white shadow-sm p-6 sm:p-8">
      {header}
      {table}
    </div>
  );
};

export default CampaignHistoryPanel;

